import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getOrder, getProducts, getPacks,
  addOrderLine, deleteOrderLine, updateOrderLine,
  runStockCheck, substituteLineItem, submitOrder,
  getOrderAudit
} from '../api';

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [packs, setPacks] = useState([]);
  const [stockResults, setStockResults] = useState(null);
  const [showAddLine, setShowAddLine] = useState(false);
  const [showSubmitResult, setShowSubmitResult] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [activeTab, setActiveTab] = useState('lines');
  const [lineForm, setLineForm] = useState({ type: 'product', ProductId: '', PackId: '', Quantity: 1 });
  const [expandedPacks, setExpandedPacks] = useState({});

  const loadOrder = useCallback(() => {
    getOrder(id).then(r => setOrder(r.data)).catch(console.error);
  }, [id]);

  useEffect(() => {
    loadOrder();
    Promise.all([getProducts(), getPacks()])
      .then(([p, pk]) => { setProducts(p.data); setPacks(pk.data); })
      .catch(console.error);
  }, [loadOrder]);

  const handleAddLine = async (e) => {
    e.preventDefault();
    try {
      const data = lineForm.type === 'pack'
        ? { PackId: lineForm.PackId, Quantity: lineForm.Quantity }
        : { ProductId: lineForm.ProductId, Quantity: lineForm.Quantity };
      await addOrderLine(id, data);
      setShowAddLine(false);
      setLineForm({ type: 'product', ProductId: '', PackId: '', Quantity: 1 });
      loadOrder();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('Remove this line?')) return;
    await deleteOrderLine(id, lineId);
    loadOrder();
  };

  const handleUpdateLine = async (lineId, field, value) => {
    await updateOrderLine(id, lineId, { [field]: value });
    loadOrder();
  };

  const handleStockCheck = async () => {
    try {
      const res = await runStockCheck(id);
      setStockResults(res.data);
      setActiveTab('stock');
      loadOrder();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleSubstitute = async (lineId, newProductId, reason) => {
    try {
      await substituteLineItem(id, lineId, {
        NewProductId: newProductId,
        Reason: reason || 'Out of stock at site',
        Note: `Substituted due to stock shortage`,
        KeepPackCode: true
      });
      // Re-run stock check
      const res = await runStockCheck(id);
      setStockResults(res.data);
      loadOrder();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleSubmit = async () => {
    if (!window.confirm('Submit this order to HubSpot/SAP?')) return;
    try {
      const res = await submitOrder(id);
      setShowSubmitResult(res.data);
      loadOrder();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleShowAudit = async () => {
    const res = await getOrderAudit(id);
    setAuditLog(res.data);
    setShowAudit(true);
  };

  const togglePackExpand = (lineId) => {
    setExpandedPacks(prev => ({ ...prev, [lineId]: !prev[lineId] }));
  };

  if (!order) return <div className="page-content"><p>Loading...</p></div>;

  const canSubmit = order.Status !== 'Submitted' && order.Status !== 'Completed' && order.Lines?.length > 0;
  const hasStockIssues = stockResults?.Results?.some(r => r.StockStatus !== 'Available');

  return (
    <>
      <div className="top-header">
        <div className="flex gap-sm" style={{ alignItems: 'center' }}>
          <button className="hs-btn hs-btn-link" onClick={() => navigate('/orders')}>← Orders</button>
          <h1>{order.OrderNumber}</h1>
          <span className={`hs-badge hs-badge-${order.Status.toLowerCase()}`}>{order.Status}</span>
        </div>
        <div className="top-header-actions">
          <button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={handleShowAudit}>Audit Log</button>
          <button className="hs-btn hs-btn-secondary" onClick={handleStockCheck} disabled={!order.Lines?.length}>
            🔍 Check Stock
          </button>
          <button className="hs-btn hs-btn-success" onClick={handleSubmit} disabled={!canSubmit}>
            📤 Submit to HubSpot
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Order Info */}
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-label">Customer</div>
            <div className="stat-value" style={{ fontSize: 18 }}>{order.CustomerName}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Franchise Site</div>
            <div className="stat-value" style={{ fontSize: 18 }}>{order.Site?.SiteName}</div>
            <div className="text-muted text-sm">{order.Site?.SiteCode} · {order.Site?.Region}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">HubSpot Deal</div>
            <div className="stat-value" style={{ fontSize: 18 }}>{order.HubSpotDealId || '—'}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Order Total</div>
            <div className="stat-value" style={{ fontSize: 24 }}>£{order.TotalAmount?.toFixed(2)}</div>
            <div className="text-muted text-sm">{order.Lines?.length || 0} line(s)</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="hs-tabs">
          <button className={`hs-tab ${activeTab === 'lines' ? 'active' : ''}`} onClick={() => setActiveTab('lines')}>
            Order Lines ({order.Lines?.length || 0})
          </button>
          <button className={`hs-tab ${activeTab === 'stock' ? 'active' : ''}`} onClick={() => setActiveTab('stock')}>
            Stock Check {stockResults ? (hasStockIssues ? '⚠️' : '✅') : ''}
          </button>
        </div>

        {/* ======================= LINES TAB ======================= */}
        {activeTab === 'lines' && (
          <div className="hs-card">
            <div className="hs-card-header">
              <h2>Order Lines</h2>
              <button className="hs-btn hs-btn-primary hs-btn-sm" onClick={() => setShowAddLine(true)}>
                + Add Line
              </button>
            </div>
            <table className="hs-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th className="text-right">Unit Price</th>
                  <th className="text-right">Line Total</th>
                  <th>Stock</th>
                  <th>Substituted?</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(!order.Lines || order.Lines.length === 0) ? (
                  <tr><td colSpan="9">
                    <div className="empty-state">
                      <div className="empty-icon">📦</div>
                      <h3>No lines yet</h3>
                      <p>Add products or packs to this order</p>
                    </div>
                  </td></tr>
                ) : (
                  order.Lines.map(line => (
                    <React.Fragment key={line.LineId}>
                      <tr>
                        <td>{line.LineNumber}</td>
                        <td>
                          {line.PackId
                            ? <span className="hs-badge hs-badge-pack">📦 Pack</span>
                            : <span className="hs-badge hs-badge-product">🔧 Product</span>}
                        </td>
                        <td>
                          <div>
                            <strong>{line.Pack?.PackName || line.Product?.ProductName || '—'}</strong>
                            <div className="text-muted text-sm font-mono">
                              {line.Pack?.PackSKU || line.Product?.SKU}
                            </div>
                          </div>
                          {line.PackId && (
                            <button className="hs-btn hs-btn-link" style={{ padding: '2px 0', fontSize: 11 }}
                              onClick={() => togglePackExpand(line.LineId)}>
                              {expandedPacks[line.LineId] ? '▼ Hide items' : '▶ Show pack items'}
                            </button>
                          )}
                        </td>
                        <td>
                          <input type="number" className="hs-input" style={{ width: 60 }}
                            value={line.Quantity} min={1}
                            onChange={(e) => handleUpdateLine(line.LineId, 'Quantity', e.target.value)} />
                        </td>
                        <td className="text-right font-mono">£{line.UnitPrice?.toFixed(2)}</td>
                        <td className="text-right font-mono"><strong>£{line.LineTotal?.toFixed(2)}</strong></td>
                        <td>
                          <span className={`hs-badge hs-badge-${line.StockStatus.toLowerCase()}`}>
                            {line.StockStatus}
                          </span>
                        </td>
                        <td>
                          {line.IsSubstituted && (
                            <div>
                              <span className="hs-badge hs-badge-substituted">Substituted</span>
                              <div className="text-sm text-muted mt-sm">
                                Was: {line.OriginalProduct?.ProductName}
                              </div>
                              {line.SubstitutionNote && (
                                <div className="text-sm" style={{ color: '#7c3aed' }}>{line.SubstitutionNote}</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <button className="hs-btn hs-btn-sm hs-btn-danger" onClick={() => handleDeleteLine(line.LineId)}>×</button>
                        </td>
                      </tr>
                      {/* Pack items expanded view */}
                      {line.PackId && expandedPacks[line.LineId] && line.PackItems && (
                        <tr>
                          <td colSpan="9" style={{ padding: 0, paddingLeft: 40 }}>
                            <div className="pack-breakdown">
                              <h4>📦 Pack Contents — {line.Pack?.PackName}</h4>
                              {line.PackItems.map((pi, idx) => (
                                <div key={idx} className="pack-item-row">
                                  <div>
                                    <strong>{pi.Product?.ProductName}</strong>
                                    <span className="text-muted text-sm" style={{ marginLeft: 8 }}>{pi.Product?.SKU}</span>
                                  </div>
                                  <div className="flex gap-sm" style={{ alignItems: 'center' }}>
                                    <span>×{pi.Quantity * line.Quantity}</span>
                                    {pi.Stock && (
                                      <StockIndicator available={pi.Stock.AvailableQty} needed={pi.Quantity * line.Quantity} />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ======================= STOCK CHECK TAB ======================= */}
        {activeTab === 'stock' && (
          <div>
            {!stockResults ? (
              <div className="hs-card">
                <div className="hs-card-body">
                  <div className="empty-state">
                    <div className="empty-icon">🔍</div>
                    <h3>No stock check run yet</h3>
                    <p>Click "Check Stock" to decompose packs and verify availability at {order.Site?.SiteName}</p>
                    <button className="hs-btn hs-btn-primary mt-md" onClick={handleStockCheck}>Run Stock Check</button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {!hasStockIssues ? (
                  <div className="hs-alert hs-alert-success">
                    ✅ All items are available at {order.Site?.SiteName}. Ready to submit!
                  </div>
                ) : (
                  <div className="hs-alert hs-alert-warning">
                    ⚠️ Some items have stock issues at {order.Site?.SiteName}. Review below and apply substitutions if needed.
                  </div>
                )}

                {stockResults.Results.map((result, idx) => (
                  <div key={idx} className="hs-card mb-md">
                    <div className="hs-card-header">
                      <div className="flex gap-sm" style={{ alignItems: 'center' }}>
                        <h2>Line {result.LineNumber}</h2>
                        <span className={`hs-badge hs-badge-${result.Type === 'Pack' ? 'pack' : 'product'}`}>
                          {result.Type}
                        </span>
                        <span className={`hs-badge hs-badge-${result.StockStatus.toLowerCase()}`}>
                          {result.StockStatus}
                        </span>
                      </div>
                    </div>
                    <div className="hs-card-body">
                      {result.Type === 'Pack' && result.PackItems ? (
                        // Pack: show each item with stock
                        <div>
                          <div className="hs-alert hs-alert-info" style={{ marginBottom: 16 }}>
                            ℹ️ This pack has been decomposed into individual items for stock checking. Substitutions can be applied per item while the order is still sent to SAP as the pack code.
                          </div>
                          {result.PackItems.map((item, iIdx) => (
                            <div key={iIdx} style={{ marginBottom: 16 }}>
                              <div className="flex-between">
                                <div>
                                  <strong>{item.Product?.ProductName}</strong>
                                  <span className="text-muted text-sm" style={{ marginLeft: 8 }}>{item.Product?.SKU}</span>
                                </div>
                                <div className="flex gap-sm" style={{ alignItems: 'center' }}>
                                  <span className="text-sm">Need: {item.Needed}</span>
                                  <StockIndicator available={item.Available} needed={item.Needed} />
                                  <span className={`hs-badge ${item.IsAvailable ? 'hs-badge-available' : 'hs-badge-outofstock'}`}>
                                    {item.IsAvailable ? 'Available' : `Short by ${item.Shortage}`}
                                  </span>
                                </div>
                              </div>

                              {/* Substitution options for out-of-stock pack items */}
                              {!item.IsAvailable && item.Substitutions.length > 0 && (
                                <div className="substitution-panel">
                                  <h4>🔄 Substitution Options for {item.Product?.ProductName}</h4>
                                  {item.Substitutions.map((sub, sIdx) => (
                                    <div key={sIdx} className="substitution-option">
                                      <div className="sub-info">
                                        <div className="sub-name">{sub.SubstituteProduct?.ProductName}</div>
                                        <div className="sub-detail">
                                          {sub.SubstituteProduct?.SKU} · £{sub.SubstituteProduct?.UnitPrice?.toFixed(2)} · Stock: {sub.AvailableQty} available
                                          {sub.Notes && ` · ${sub.Notes}`}
                                        </div>
                                      </div>
                                      <button
                                        className="hs-btn hs-btn-sm hs-btn-primary"
                                        disabled={sub.AvailableQty < item.Needed}
                                        onClick={() => handleSubstitute(result.LineId, sub.SubstituteProductId, sub.Notes)}>
                                        {sub.AvailableQty >= item.Needed ? 'Use This' : 'No Stock'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {!item.IsAvailable && item.Substitutions.length === 0 && (
                                <div className="hs-alert hs-alert-danger" style={{ marginTop: 8 }}>
                                  No substitution rules defined for this product.
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Single product
                        <div>
                          <div className="flex-between">
                            <span>Required: {result.Needed} · Available: {result.Available}</span>
                            <StockIndicator available={result.Available} needed={result.Needed} />
                          </div>

                          {result.StockStatus !== 'Available' && result.Substitutions?.length > 0 && (
                            <div className="substitution-panel">
                              <h4>🔄 Substitution Options</h4>
                              {result.Substitutions.map((sub, sIdx) => (
                                <div key={sIdx} className="substitution-option">
                                  <div className="sub-info">
                                    <div className="sub-name">{sub.SubstituteProduct?.ProductName}</div>
                                    <div className="sub-detail">
                                      {sub.SubstituteProduct?.SKU} · £{sub.SubstituteProduct?.UnitPrice?.toFixed(2)} · Stock: {sub.AvailableQty}
                                      {sub.Notes && ` · ${sub.Notes}`}
                                    </div>
                                  </div>
                                  <button
                                    className="hs-btn hs-btn-sm hs-btn-primary"
                                    disabled={sub.AvailableQty < result.Needed}
                                    onClick={() => handleSubstitute(result.LineId, sub.SubstituteProductId, sub.Notes)}>
                                    {sub.AvailableQty >= result.Needed ? 'Substitute' : 'No Stock'}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Line Modal */}
      {showAddLine && (
        <div className="hs-modal-overlay" onClick={() => setShowAddLine(false)}>
          <div className="hs-modal" onClick={e => e.stopPropagation()}>
            <div className="hs-modal-header">
              <h3>Add Order Line</h3>
              <button className="hs-modal-close" onClick={() => setShowAddLine(false)}>×</button>
            </div>
            <form onSubmit={handleAddLine}>
              <div className="hs-modal-body">
                <div className="hs-form-group">
                  <label>Item Type</label>
                  <div className="flex gap-sm">
                    <button type="button"
                      className={`hs-btn ${lineForm.type === 'product' ? 'hs-btn-primary' : 'hs-btn-secondary'}`}
                      onClick={() => setLineForm({ ...lineForm, type: 'product', PackId: '' })}>
                      🔧 Product
                    </button>
                    <button type="button"
                      className={`hs-btn ${lineForm.type === 'pack' ? 'hs-btn-primary' : 'hs-btn-secondary'}`}
                      onClick={() => setLineForm({ ...lineForm, type: 'pack', ProductId: '' })}>
                      📦 Pack
                    </button>
                  </div>
                </div>

                {lineForm.type === 'product' ? (
                  <div className="hs-form-group">
                    <label>Product *</label>
                    <select className="hs-select" required value={lineForm.ProductId}
                      onChange={e => setLineForm({ ...lineForm, ProductId: e.target.value })}>
                      <option value="">Select product...</option>
                      {products.map(p => (
                        <option key={p.ProductId} value={p.ProductId}>
                          {p.ProductName} ({p.SKU}) — £{p.UnitPrice.toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="hs-form-group">
                    <label>Pack *</label>
                    <select className="hs-select" required value={lineForm.PackId}
                      onChange={e => setLineForm({ ...lineForm, PackId: e.target.value })}>
                      <option value="">Select pack...</option>
                      {packs.map(p => (
                        <option key={p.PackId} value={p.PackId}>
                          {p.PackName} ({p.PackSKU}) — £{p.PackPrice.toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="hs-form-group">
                  <label>Quantity</label>
                  <input type="number" className="hs-input" min={1} value={lineForm.Quantity}
                    onChange={e => setLineForm({ ...lineForm, Quantity: parseInt(e.target.value) || 1 })} />
                </div>

                {/* Show pack contents preview */}
                {lineForm.type === 'pack' && lineForm.PackId && (() => {
                  const pack = packs.find(p => p.PackId === parseInt(lineForm.PackId));
                  return pack?.Items ? (
                    <div className="pack-breakdown">
                      <h4>📦 Pack contains:</h4>
                      {pack.Items.map((item, i) => (
                        <div key={i} className="pack-item-row">
                          <span>{item.Product?.ProductName} ({item.Product?.SKU})</span>
                          <span>×{item.Quantity}</span>
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="hs-modal-footer">
                <button type="button" className="hs-btn hs-btn-secondary" onClick={() => setShowAddLine(false)}>Cancel</button>
                <button type="submit" className="hs-btn hs-btn-primary">Add Line</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Submit Result Modal */}
      {showSubmitResult && (
        <div className="hs-modal-overlay" onClick={() => setShowSubmitResult(null)}>
          <div className="hs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 800 }}>
            <div className="hs-modal-header">
              <h3>✅ Order Submitted Successfully</h3>
              <button className="hs-modal-close" onClick={() => setShowSubmitResult(null)}>×</button>
            </div>
            <div className="hs-modal-body">
              <div className="hs-alert hs-alert-success">
                Order {showSubmitResult.Order?.OrderNumber} has been submitted.
              </div>

              <h4 style={{ marginBottom: 12 }}>SAP Payload Preview:</h4>
              <pre style={{
                background: '#1e293b', color: '#e2e8f0', padding: 16,
                borderRadius: 8, fontSize: 12, overflow: 'auto', maxHeight: 300
              }}>
                {JSON.stringify(showSubmitResult.SAPPayload, null, 2)}
              </pre>

              <div className="hs-alert hs-alert-info mt-md">
                <strong>HubSpot Sync:</strong> {showSubmitResult.HubSpotSync?.message}
              </div>
            </div>
            <div className="hs-modal-footer">
              <button className="hs-btn hs-btn-primary" onClick={() => setShowSubmitResult(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAudit && (
        <div className="hs-modal-overlay" onClick={() => setShowAudit(false)}>
          <div className="hs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="hs-modal-header">
              <h3>Audit Log — {order.OrderNumber}</h3>
              <button className="hs-modal-close" onClick={() => setShowAudit(false)}>×</button>
            </div>
            <div className="hs-modal-body">
              {auditLog.length === 0 ? (
                <p className="text-muted">No audit entries yet.</p>
              ) : (
                <table className="hs-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Line</th>
                      <th>By</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.map(log => (
                      <tr key={log.LogId}>
                        <td><span className="hs-badge hs-badge-draft">{log.Action}</span></td>
                        <td>{log.LineId || '—'}</td>
                        <td>{log.PerformedBy}</td>
                        <td className="text-sm text-muted">{new Date(log.PerformedAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="hs-modal-footer">
              <button className="hs-btn hs-btn-secondary" onClick={() => setShowAudit(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StockIndicator({ available, needed }) {
  const pct = needed > 0 ? Math.min((available / needed) * 100, 100) : 100;
  const level = pct >= 100 ? 'high' : pct >= 50 ? 'medium' : 'low';
  return (
    <div className="stock-indicator">
      <div className="stock-bar">
        <div className={`stock-bar-fill ${level}`} style={{ width: `${pct}%` }}></div>
      </div>
      <span className="text-sm">{available}</span>
    </div>
  );
}
